// Template Registry - maps template IDs to render functions
import type { TemplateData, TemplateMetadata, TemplateRegistration } from './types';
import { DESIGN_SYSTEMS as _designSystems } from '../../ai/designSystem';
export type { TemplateData, TemplateMetadata, TemplateRegistration, BusinessInfo, CustomTheme, Seo, Service, Testimonial, FaqItem, PriceItem, Promotion } from './types';
export { buildPlaceholderData, buildTemplatePreviewData } from './placeholder-data';

// Import all templates
import { boldImpact } from './bold-impact';
import { minimalZen } from './minimal-zen';
import { luxuryNoir } from './luxury-noir';
import { classicEditorial } from './classic-editorial';
import { techTerminal } from './tech-terminal';
import { creativePlayful } from './creative-playful';
import { corporateClean } from './corporate-clean';
import { healthWellness } from './health-wellness';
import { foodBistro } from './food-bistro';
import { modernGradient } from './modern-gradient';
import { neonNights } from './neon-nights';
import { warmArtisan } from './warm-artisan';
import { nordicLight } from './nordic-light';
import { glassMorph } from './glass-morph';
import { retroPop } from './retro-pop';
import { darkElegance } from './dark-elegance';
import { freshNature } from './fresh-nature';
import { studioMinimal } from './studio-minimal';
import { ivoryTower } from './ivory-tower';
import { royalCrest } from './royal-crest';
import { timberLodge } from './timber-lodge';
import { marbleArch } from './marble-arch';
import { vintagePress } from './vintage-press';
import { sunsetHorizon } from './sunset-horizon';
import { arcticFrost } from './arctic-frost';
import { copperForge } from './copper-forge';
import { electricViolet } from './electric-violet';
import { mosaicTile } from './mosaic-tile';
import { paperFold } from './paper-fold';
import { monoType } from './mono-type';
import { sandDune } from './sand-dune';
import { skyCanvas } from './sky-canvas';
import { slateEdge } from './slate-edge';
import { candyShop } from './candy-shop';
import { jungleTrek } from './jungle-trek';
import { pixelGrid } from './pixel-grid';
import { oceanDrift } from './ocean-drift';
import { terraCotta } from './terra-cotta';

const TEMPLATE_REGISTRY: Map<string, TemplateRegistration> = new Map();

function register(reg: TemplateRegistration) {
  TEMPLATE_REGISTRY.set(reg.meta.id, reg);
}

register(boldImpact);
register(minimalZen);
register(luxuryNoir);
register(classicEditorial);
register(techTerminal);
register(creativePlayful);
register(corporateClean);
register(healthWellness);
register(foodBistro);
register(modernGradient);
register(neonNights);
register(warmArtisan);
register(nordicLight);
register(glassMorph);
register(retroPop);
register(darkElegance);
register(freshNature);
register(studioMinimal);
register(ivoryTower);
register(royalCrest);
register(timberLodge);
register(marbleArch);
register(vintagePress);
register(sunsetHorizon);
register(arcticFrost);
register(copperForge);
register(electricViolet);
register(mosaicTile);
register(paperFold);
register(monoType);
register(sandDune);
register(skyCanvas);
register(slateEdge);
register(candyShop);
register(jungleTrek);
register(pixelGrid);
register(oceanDrift);
register(terraCotta);

// Legacy template ID mapping (old 50 IDs -> new 18 IDs)
const LEGACY_MAP: Record<string, string> = {
  // Modern
  'modern-gradient': 'modern-gradient',
  'modern-glass': 'glass-morph',
  'modern-neon': 'neon-nights',
  'modern-mesh': 'modern-gradient',
  'modern-aurora': 'fresh-nature',
  // Classic
  'classic-elegant': 'classic-editorial',
  'classic-traditional': 'classic-editorial',
  'classic-vintage': 'warm-artisan',
  'classic-scholarly': 'classic-editorial',
  'classic-heritage': 'classic-editorial',
  // Bold
  'bold-fire': 'bold-impact',
  'bold-electric': 'neon-nights',
  'bold-midnight': 'dark-elegance',
  'bold-impact': 'bold-impact',
  'bold-contrast': 'bold-impact',
  // Minimal
  'minimal-clean': 'minimal-zen',
  'minimal-mono': 'studio-minimal',
  'minimal-zen': 'minimal-zen',
  'minimal-nordic': 'nordic-light',
  'minimal-paper': 'studio-minimal',
  // Creative
  'creative-playful': 'creative-playful',
  'creative-artsy': 'creative-playful',
  'creative-retro': 'retro-pop',
  'creative-pop': 'retro-pop',
  'creative-brush': 'creative-playful',
  // Corporate
  'corporate-professional': 'corporate-clean',
  'corporate-executive': 'corporate-clean',
  'corporate-startup': 'corporate-clean',
  'corporate-consulting': 'corporate-clean',
  'corporate-finance': 'corporate-clean',
  // Luxury
  'luxury-gold': 'luxury-noir',
  'luxury-noir': 'luxury-noir',
  'luxury-marble': 'luxury-noir',
  'luxury-royal': 'dark-elegance',
  'luxury-champagne': 'warm-artisan',
  // Tech
  'tech-cyber': 'tech-terminal',
  'tech-matrix': 'tech-terminal',
  'tech-ai': 'tech-terminal',
  'tech-terminal': 'tech-terminal',
  'tech-dark': 'tech-terminal',
  // Health
  'health-wellness': 'health-wellness',
  'health-medical': 'health-wellness',
  'health-spa': 'health-wellness',
  'health-fitness': 'bold-impact',
  'health-natural': 'fresh-nature',
  // Food
  'food-restaurant': 'food-bistro',
  'food-cafe': 'food-bistro',
  'food-bakery': 'warm-artisan',
  'food-fine-dining': 'luxury-noir',
  'food-delivery': 'retro-pop',
  // Legacy templates
  'modern-minimal': 'minimal-zen',
  'bold-corporate': 'corporate-clean',
  'warm-friendly': 'warm-artisan',
  'tech-startup': 'tech-terminal',
  'elegant-luxury': 'luxury-noir',
};

export function getTemplateRegistry(): ReadonlyMap<string, TemplateRegistration> {
  return TEMPLATE_REGISTRY;
}

export function getAllTemplateMetadata(): TemplateMetadata[] {
  return Array.from(TEMPLATE_REGISTRY.values()).map(r => r.meta);
}

// Canonical theme defaults for every template, extracted from their CSS :root blocks.
// This is the single source of truth - DESIGN_SYSTEMS is a fallback for legacy IDs only.
const TEMPLATE_THEMES: Record<string, { primaryColor: string; accentColor: string; bgColor: string; textColor: string; bodyTextColor: string; headingFont: string; bodyFont: string }> = {
  'arctic-frost':      { primaryColor: '#4FC3F7', accentColor: '#4FC3F7', bgColor: '#37474F', textColor: '#ECEFF1', bodyTextColor: '#90A4AE', headingFont: 'Rajdhani', bodyFont: 'Nunito' },
  'bold-impact':       { primaryColor: '#eab308', accentColor: '#eab308', bgColor: '#0c0a09', textColor: '#fafaf9', bodyTextColor: '#a8a29e', headingFont: 'Anton', bodyFont: 'Inter' },
  'candy-shop':        { primaryColor: '#EC4899', accentColor: '#FBBF24', bgColor: '#FFF1F2', textColor: '#1F1F1F', bodyTextColor: '#6B6B6B', headingFont: 'Fredoka', bodyFont: 'Poppins' },
  'classic-editorial': { primaryColor: '#2563eb', accentColor: '#2563eb', bgColor: '#faf9f7', textColor: '#1e293b', bodyTextColor: '#64748b', headingFont: 'Playfair Display', bodyFont: 'Source Serif Pro' },
  'copper-forge':      { primaryColor: '#B87333', accentColor: '#B87333', bgColor: '#2D2D2D', textColor: '#E8E8E8', bodyTextColor: '#999999', headingFont: 'Bebas Neue', bodyFont: 'Karla' },
  'corporate-clean':   { primaryColor: '#1e40af', accentColor: '#3b82f6', bgColor: '#ffffff', textColor: '#0f172a', bodyTextColor: '#64748b', headingFont: 'Plus Jakarta Sans', bodyFont: 'Plus Jakarta Sans' },
  'creative-playful':  { primaryColor: '#8b5cf6', accentColor: '#ec4899', bgColor: '#ffffff', textColor: '#1e293b', bodyTextColor: '#64748b', headingFont: 'Baloo 2', bodyFont: 'Nunito' },
  'dark-elegance':     { primaryColor: '#7e22ce', accentColor: '#eab308', bgColor: '#0c0015', textColor: '#f3e8ff', bodyTextColor: '#c4b5d0', headingFont: 'Bodoni Moda', bodyFont: 'EB Garamond' },
  'electric-violet':   { primaryColor: '#7C3AED', accentColor: '#84CC16', bgColor: '#0F0720', textColor: '#F5F3FF', bodyTextColor: '#A78BFA', headingFont: 'Space Grotesk', bodyFont: 'DM Sans' },
  'food-bistro':       { primaryColor: '#b91c1c', accentColor: '#ca8a04', bgColor: '#1c1917', textColor: '#fef2f2', bodyTextColor: '#c4b5a5', headingFont: 'Playfair Display', bodyFont: 'Lato' },
  'fresh-nature':      { primaryColor: '#16a34a', accentColor: '#a16207', bgColor: '#f0fdf4', textColor: '#14532d', bodyTextColor: '#365e3d', headingFont: 'Quicksand', bodyFont: 'Nunito' },
  'glass-morph':       { primaryColor: '#3b82f6', accentColor: '#8b5cf6', bgColor: '#020617', textColor: '#f1f5f9', bodyTextColor: '#94a3b8', headingFont: 'Poppins', bodyFont: 'Inter' },
  'health-wellness':   { primaryColor: '#10b981', accentColor: '#14b8a6', bgColor: '#f0fdf4', textColor: '#064e3b', bodyTextColor: '#4b7c6f', headingFont: 'Comfortaa', bodyFont: 'Nunito' },
  'ivory-tower':       { primaryColor: '#800020', accentColor: '#800020', bgColor: '#FFFFF0', textColor: '#2C1810', bodyTextColor: '#6B5B4F', headingFont: 'Cormorant Garamond', bodyFont: 'Lato' },
  'jungle-trek':       { primaryColor: '#166534', accentColor: '#EA580C', bgColor: '#F0FDF4', textColor: '#14532D', bodyTextColor: '#4D7C5C', headingFont: 'Signika', bodyFont: 'Cabin' },
  'luxury-noir':       { primaryColor: '#c9a96e', accentColor: '#c9a96e', bgColor: '#0a0a0a', textColor: '#f5f0e8', bodyTextColor: '#bdb5a8', headingFont: 'Cinzel', bodyFont: 'Cormorant' },
  'marble-arch':       { primaryColor: '#1B4332', accentColor: '#1B4332', bgColor: '#FAFAFA', textColor: '#1A1A1A', bodyTextColor: '#6B6B6B', headingFont: 'Libre Baskerville', bodyFont: 'Manrope' },
  'minimal-zen':       { primaryColor: '#a8a29e', accentColor: '#a8a29e', bgColor: '#fafaf9', textColor: '#44403c', bodyTextColor: '#78716c', headingFont: 'Noto Serif', bodyFont: 'Noto Sans' },
  'modern-gradient':   { primaryColor: '#7c3aed', accentColor: '#a855f7', bgColor: '#0f0f1a', textColor: '#f8fafc', bodyTextColor: '#94a3b8', headingFont: 'Inter', bodyFont: 'Inter' },
  'mono-type':         { primaryColor: '#000000', accentColor: '#000000', bgColor: '#FFFFFF', textColor: '#000000', bodyTextColor: '#666666', headingFont: 'JetBrains Mono', bodyFont: 'Sora' },
  'mosaic-tile':       { primaryColor: '#0D9488', accentColor: '#F59E0B', bgColor: '#FAFAF9', textColor: '#1C1917', bodyTextColor: '#57534E', headingFont: 'Archivo', bodyFont: 'Outfit' },
  'neon-nights':       { primaryColor: '#00f5d4', accentColor: '#f15bb5', bgColor: '#0a0a0f', textColor: '#e2e8f0', bodyTextColor: '#8892a8', headingFont: 'Outfit', bodyFont: 'Inter' },
  'nordic-light':      { primaryColor: '#0ea5e9', accentColor: '#0ea5e9', bgColor: '#f8fafc', textColor: '#334155', bodyTextColor: '#64748b', headingFont: 'Quicksand', bodyFont: 'Nunito' },
  'ocean-drift':       { primaryColor: '#0077B6', accentColor: '#FF8A65', bgColor: '#F0F9FF', textColor: '#0C4A6E', bodyTextColor: '#4A90A4', headingFont: 'Abril Fatface', bodyFont: 'Hind' },
  'paper-fold':        { primaryColor: '#333333', accentColor: '#333333', bgColor: '#F8F7F4', textColor: '#1A1A1A', bodyTextColor: '#6B6B6B', headingFont: 'IBM Plex Sans', bodyFont: 'Work Sans' },
  'pixel-grid':        { primaryColor: '#06B6D4', accentColor: '#D946EF', bgColor: '#111827', textColor: '#F9FAFB', bodyTextColor: '#9CA3AF', headingFont: 'Press Start 2P', bodyFont: 'Lexend' },
  'retro-pop':         { primaryColor: '#dc2626', accentColor: '#2563eb', bgColor: '#fefce8', textColor: '#18181b', bodyTextColor: '#3f3f46', headingFont: 'Bungee', bodyFont: 'Inter' },
  'royal-crest':       { primaryColor: '#1E3A8A', accentColor: '#D4AF37', bgColor: '#0F1B3D', textColor: '#E2E8F0', bodyTextColor: '#94A3B8', headingFont: 'EB Garamond', bodyFont: 'Open Sans' },
  'sand-dune':         { primaryColor: '#3E2723', accentColor: '#D2B48C', bgColor: '#FAF6F0', textColor: '#3E2723', bodyTextColor: '#7C6354', headingFont: 'Quicksand', bodyFont: 'Mulish' },
  'sky-canvas':        { primaryColor: '#0F172A', accentColor: '#0EA5E9', bgColor: '#FFFFFF', textColor: '#0F172A', bodyTextColor: '#64748B', headingFont: 'Plus Jakarta Sans', bodyFont: 'Figtree' },
  'slate-edge':        { primaryColor: '#1E293B', accentColor: '#64748B', bgColor: '#F1F5F9', textColor: '#0F172A', bodyTextColor: '#64748B', headingFont: 'Geist', bodyFont: 'Atkinson Hyperlegible' },
  'studio-minimal':    { primaryColor: '#dc2626', accentColor: '#000000', bgColor: '#ffffff', textColor: '#000000', bodyTextColor: '#6b7280', headingFont: 'Space Mono', bodyFont: 'IBM Plex Sans' },
  'sunset-horizon':    { primaryColor: '#FF6B6B', accentColor: '#FF6B6B', bgColor: '#2C3E50', textColor: '#ECF0F1', bodyTextColor: '#95A5A6', headingFont: 'Josefin Sans', bodyFont: 'Source Sans 3' },
  'tech-terminal':     { primaryColor: '#22c55e', accentColor: '#3b82f6', bgColor: '#0f172a', textColor: '#e2e8f0', bodyTextColor: '#94a3b8', headingFont: 'JetBrains Mono', bodyFont: 'Fira Code' },
  'terra-cotta':       { primaryColor: '#C2703E', accentColor: '#87A878', bgColor: '#FFF9F5', textColor: '#3D2B1F', bodyTextColor: '#7C6354', headingFont: 'Fraunces', bodyFont: 'Public Sans' },
  'timber-lodge':      { primaryColor: '#6B4226', accentColor: '#6B4226', bgColor: '#FFF8E7', textColor: '#2D1B0E', bodyTextColor: '#7C6354', headingFont: 'Playfair Display', bodyFont: 'Rubik' },
  'vintage-press':     { primaryColor: '#704214', accentColor: '#704214', bgColor: '#F5E6CC', textColor: '#2C1810', bodyTextColor: '#6B5340', headingFont: 'Merriweather', bodyFont: 'PT Sans' },
  'warm-artisan':      { primaryColor: '#c2410c', accentColor: '#c2410c', bgColor: '#fefdf8', textColor: '#422006', bodyTextColor: '#78716c', headingFont: 'Cormorant Garamond', bodyFont: 'EB Garamond' },
};

export function getTemplateTheme(templateType: string): { primaryColor?: string; accentColor?: string; bgColor?: string; textColor?: string; bodyTextColor?: string; headingFont?: string; bodyFont?: string } {
  const resolvedId = LEGACY_MAP[templateType] ?? templateType;
  // Check the canonical template themes first (covers all current templates)
  const tt = TEMPLATE_THEMES[resolvedId] || TEMPLATE_THEMES[templateType];
  if (tt) return tt;
  // Fallback to DESIGN_SYSTEMS for legacy template IDs
  const ds = _designSystems[templateType] || _designSystems[resolvedId];
  if (!ds) return {};
  return {
    primaryColor: ds.colorTokens.primary,
    accentColor: ds.colorTokens.accent,
    bgColor: ds.colorTokens.bg,
    textColor: ds.colorTokens.text,
    bodyTextColor: ds.colorTokens.textMuted,
    headingFont: ds.typographyTokens.fontHeading,
    bodyFont: ds.typographyTokens.fontBody,
  };
}

// CSS + JS injected into every generated site for proper mobile nav behavior.
const MOBILE_NAV_INJECT = `<style>
@media(max-width:768px){
  html{scroll-padding-top:70px}
  .nav-inner,.de-nav-inner,.fn-nav-inner,.sm-nav-inner{position:relative}
  .nav-links.show,.de-nav-links.show,.fn-nav-links.show,.sm-nav-links.show{z-index:999}
}
</style>
<script>
(function(){
  document.addEventListener('click',function(e){
    var link=e.target.closest('a[href]');
    if(link){var c=link.parentElement;while(c){if(c.classList.contains('show')){c.classList.remove('show');return}c=c.parentElement}}
    var toggle=e.target.closest('[class*=nav-toggle]');
    if(!toggle){document.querySelectorAll('.show').forEach(function(el){if(/nav-link/.test(el.className))el.classList.remove('show')})}
  });
})();
<\/script>`;

export function renderTemplate(templateType: string, data: TemplateData, enabledFeatures?: string[]): string {
  const resolvedId = LEGACY_MAP[templateType] ?? templateType;
  const reg = TEMPLATE_REGISTRY.get(resolvedId) ?? TEMPLATE_REGISTRY.get('bold-impact')!;
  const featureSet = new Set(enabledFeatures ?? reg.meta.supportedFeatures);
  let html = reg.render(data, featureSet);
  // Inject mobile nav CSS + script before </body>
  html = html.replace('</body>', MOBILE_NAV_INJECT + '</body>');
  return html;
}
